import React, { useState, useRef, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import "./PdfUploader.css";

function PdfUploader() {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [caseId, setCaseId] = useState(null);
  const [caseData, setCaseData] = useState(null);

  const fileInputRef = useRef(null);
  const history = useHistory();

  // âœ… Load caseId from localStorage
  useEffect(() => {
    const storedCaseId = localStorage.getItem("caseId");

    if (storedCaseId && storedCaseId !== "undefined" && storedCaseId !== "null") {
      setCaseId(storedCaseId);
      console.log("caseId loaded:", storedCaseId);
    } else {
      console.warn("No valid caseId found in localStorage");
      setError("No active case found.");
    }
  }, []);

  // ðŸ” Load case details only if caseId is valid
  useEffect(() => {
    if (!caseId) return; // â— Prevent API call with null/undefined

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      setError("API_URL is not defined in env.js");
      return;
    }

    const fetchCaseDetails = async () => {
      try {
        console.log(`Fetching case: ${apiUrl}/api/case-details/${caseId}`);

        const response = await fetch(`${apiUrl}/api/case-details/${caseId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });

        if (response.status === 404) {
          setError("This case does not exist anymore (404).");
          return;
        }

        if (!response.ok) {
          throw new Error(`Error loading case: ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.case) {
          setError("Server returned an empty case.");
          return;
        }

        setCaseData(data.case);
        console.log("Case data loaded:", data.case);

      } catch (err) {
        console.error("Case loading error:", err);
        setError(`Unable to load case: ${err.message}`);
      }
    };

    fetchCaseDetails();
  }, [caseId]);

  // ðŸ“ Select PDF
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("âŒ Please select a valid PDF file.");
      setPdfFile(null);
      setPdfUrl(null);
      return;
    }

    setError("");
    setPdfFile(file);
    setPdfUrl(URL.createObjectURL(file));
  };

  // âŒ Remove PDF
  const handleRemoveFile = () => {
    setPdfFile(null);
    setPdfUrl(null);
    setError("");
    setMessage("");
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  // ðŸš€ Upload to backend
  const handleUpload = async () => {
    if (!pdfFile) {
      setError(" No file selected!");
      return;
    }

    if (!caseId) {
      setError(" No case ID detected.");
      return;
    }

    setUploading(true);
    setMessage("â³ Uploading...");
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", pdfFile);

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error("API_URL is not defined!");

      const uploadUrl = `${apiUrl}/api/case/${caseId}/upload-pdf`;

      console.log("Uploading to:", uploadUrl);

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      const contentType = response.headers.get("content-type");

      let data = null;
      if (contentType?.includes("application/json")) {
        data = await response.json();
      } else {
        throw new Error(`Server returned non-JSON response (${response.status})`);
      }

      if (!response.ok) {
        throw new Error(data?.detail || data?.message || `Server error ${response.status}`);
      }

      setMessage("âœ… PDF successfully uploaded!");
    } catch (err) {
      console.error("Upload FAIL:", err);
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ðŸŽ¨ UI rendering
  return (
    <>
      <div className="pdf-uploader-container">
        <div className="pdf-uploader-card">
          <h2 className="pdf-uploader-title">Upload Case PDF</h2>

          {/* Case information */}
          {caseData ? (
            <div className="analysis-header-card">
              <h3 className="analysis-company-name">
                {caseData.company_name || caseData.enterpriseName}
              </h3>

              <p className="analysis-info-line">
                <span className="analysis-icon"></span>
                <strong>Sector: </strong> {caseData.activity_sector}
                <span style={{ margin: "0 6px" }}></span>
                <strong>Size: </strong> {caseData.company_size || "-"} employees
              </p>

              <p className="analysis-info-line">
                <span className="analysis-icon"></span>
                <strong>Created on: </strong>
                {new Date(caseData.timestamp).toLocaleString()}
              </p>

            </div>
          ) : (
            <p style={{ color: "black", fontWeight: "bold" }}>
              loading...
            </p>
          )}

          {/* PDF selection */}
          <label className="pdf-button upload">
            Select PDF
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </label>

          {error && <p style={{ color: "red" }}>{error}</p>}

          {pdfFile && (
            <>
              <p style={{ marginTop: "15px" }}>
                <strong>{pdfFile.name}</strong>
              </p>

              <div className="pdf-buttons">
                <button
                  className="pdf-button upload"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>

                <button className="pdf-button delete" onClick={handleRemoveFile}>
                  Remove
                </button>
              </div>
            </>
          )}

          {message && (
            <p style={{ marginTop: "15px", color: "green", fontWeight: 500 }}>
              {message}
            </p>
          )}

          {pdfUrl && (
            <iframe src={pdfUrl} className="pdf-preview" title="PDF Preview" />
          )}
        </div>
      </div>

      <div className="icam-navigation">
        <Link to="/visual-management-1">
          <button className="icam-nav-btn">Back to visual management</button>
        </Link>
        <Link to="/analysisbeta">
          <button className="icam-nav-btn">Move to Analysis</button>
        </Link>
      </div>
    </>
  );
}

export default PdfUploader;
